import React, { useState, useEffect } from 'react';
import { supportAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Loader2, CheckCircle, Send } from 'lucide-react';

export const SupportForm = ({ onSuccess, onCancel }) => {
    const { user, isAuthenticated } = useAuth();
    
    // Form state
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [category, setCategory] = useState('general');
    
    // Hidden fields
    const [formToken, setFormToken] = useState('');
    const [honeypot, setHoneypot] = useState(''); // Should stay empty
    
    // UI state
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [ticketId, setTicketId] = useState('');
    const [categories, setCategories] = useState([]);

    // Fetch form token and categories on mount
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                // Get form token (for timing validation)
                const tokenRes = await supportAPI.getFormToken();
                setFormToken(tokenRes.data.token);
                
                // Get categories
                const catRes = await supportAPI.getCategories();
                setCategories(catRes.data.categories);
            } catch (err) {
                console.error('Failed to initialize support form:', err);
                setError('Failed to load form. Please refresh and try again.');
            } finally {
                setLoading(false);
            }
        };
        
        init();
        
        // Auto-populate for logged in users
        if (isAuthenticated && user) {
            setEmail(user.email || '');
            setName(user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : '');
        }
    }, [isAuthenticated, user]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);
        
        try {
            const response = await supportAPI.submitForm({
                name: name.trim(),
                email: email.trim(),
                message: message.trim(),
                category,
                form_token: formToken,
                honeypot // Should be empty
            });
            
            if (response.data.success) {
                setSuccess(true);
                setTicketId(response.data.ticket_id);
                if (onSuccess) {
                    setTimeout(() => onSuccess(response.data.ticket_id), 2000);
                }
            }
        } catch (err) {
            const errorMessage = err.response?.data?.detail || 'Failed to send message. Please try again.';
            setError(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-accent" />
            </div>
        );
    }

    // Success state
    if (success) {
        return (
            <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Message Sent!</h3>
                <p className="text-muted-foreground mb-2">
                    Your reference number is <strong>#{ticketId}</strong>
                </p>
                <p className="text-sm text-muted-foreground">
                    We'll respond within 1 business day.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="support-form" aria-labelledby="support-form-title">
            <h3 id="support-form-title" className="sr-only">Contact Support Form</h3>
            
            {error && (
                <div 
                    className="p-3 rounded-md bg-destructive/10 text-destructive text-sm" 
                    data-testid="support-error"
                    role="alert"
                    aria-live="polite"
                >
                    {error}
                </div>
            )}

            {/* Honeypot field - invisible to users, bots will fill it */}
            <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                    type="text"
                    id="website"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="support-name">Name</Label>
                <Input
                    id="support-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    required
                    minLength={2}
                    maxLength={100}
                    readOnly={isAuthenticated && user?.first_name}
                    className={isAuthenticated && user?.first_name ? 'bg-secondary/50' : ''}
                    data-testid="support-name-input"
                    autoComplete="name"
                    aria-required="true"
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="support-email">Email</Label>
                <Input
                    id="support-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    readOnly={isAuthenticated}
                    className={isAuthenticated ? 'bg-secondary/50' : ''}
                    data-testid="support-email-input"
                    autoComplete="email"
                    aria-required="true"
                    aria-describedby={isAuthenticated ? "email-account-hint" : undefined}
                />
                {isAuthenticated && (
                    <p id="email-account-hint" className="text-xs text-muted-foreground">
                        Using your account email for faster support.
                    </p>
                )}
            </div>

            <div className="space-y-2">
                <Label htmlFor="support-category">Category</Label>
                <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger 
                        id="support-category" 
                        data-testid="support-category-select"
                        aria-label="Select support category"
                    >
                        <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                        {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                                {cat.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="space-y-2">
                <Label htmlFor="support-message">Message</Label>
                <Textarea
                    id="support-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Describe your question or issue..."
                    required
                    maxLength={5000}
                    rows={5}
                    className="resize-none"
                    data-testid="support-message-input"
                    aria-required="true"
                    aria-describedby="message-counter"
                />
                <p id="message-counter" className="text-xs text-muted-foreground text-right" aria-live="polite">
                    {message.length}/5000 characters
                </p>
            </div>

            <div className="flex gap-3 pt-2">
                {onCancel && (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onCancel}
                        disabled={submitting}
                        className="flex-1"
                    >
                        Cancel
                    </Button>
                )}
                <Button
                    type="submit"
                    disabled={submitting || !name.trim() || !email.trim() || !message.trim()}
                    className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90"
                    data-testid="support-submit-btn"
                    aria-busy={submitting}
                >
                    {submitting ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                            <span>Sending...</span>
                        </>
                    ) : (
                        <>
                            <Send className="mr-2 h-4 w-4" aria-hidden="true" />
                            Send Message
                        </>
                    )}
                </Button>
            </div>

            <p className="text-xs text-muted-foreground text-center pt-2">
                Support requests are typically answered within 1 business day.
            </p>
        </form>
    );
};

export default SupportForm;
